import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

interface UserContextType {
  user: {
    id: string;
    username: string;
    displayName: string | null;
    bio: string | null;
    avatarUrl: string | null;
    coverImage: string | null;
    spotifyConnected: boolean;
    appleMusicConnected: boolean;
  } | null;
  loading: boolean;
  error: string | null;
  updateProfile: (data: Partial<UserContextType['user']>) => Promise<void>;
  retryLoading: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export function UserProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserContextType['user']>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [authChecked, setAuthChecked] = useState(false);

  const handleDeletedUser = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setError('This account has been deleted. Please create a new account to continue.');
  };

  const createUserProfile = async (userId: string, email: string | undefined) => {
    const username = email ? email.split('@')[0] : `user_${userId.slice(0, 8)}`;

    const { data: newUser, error: createError } = await supabase
      .from('users')
      .insert([
        {
          id: userId,
          username,
          display_name: null,
          bio: null,
          avatar_url: null,
          cover_image: null,
          spotify_connected: false,
          apple_music_connected: false,
        }
      ])
      .select()
      .single();

    if (createError) {
      throw createError;
    }

    return newUser;
  };

  const fetchOrCreateUser = async (userId: string) => {
    try {
      // Check if user exists in auth
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) {
        throw new Error('User not found');
      }

      // First try to fetch the existing user
      const { data: existingUser, error: fetchError } = await supabase
        .from('users')
        .select()
        .eq('id', userId)
        .maybeSingle();

      // If we successfully found the user, return it
      if (!fetchError && existingUser) {
        return existingUser;
      }

      // If there was an error other than "no rows returned", throw it
      if (fetchError && fetchError.code !== 'PGRST116') {
        throw fetchError;
      }

      // Create new user profile
      const { data: { session } } = await supabase.auth.getSession();
      return await createUserProfile(userId, session?.user?.email);
    } catch (error) {
      console.error('Error in fetchOrCreateUser:', error);
      throw error;
    }
  };

  const initUser = async () => {
    try {
      setError(null);
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session?.user) {
        const profile = await fetchOrCreateUser(session.user.id);

        if (profile) {
          setUser({
            id: profile.id,
            username: profile.username,
            displayName: profile.display_name,
            bio: profile.bio,
            avatarUrl: profile.avatar_url,
            coverImage: profile.cover_image,
            spotifyConnected: profile.spotify_connected,
            appleMusicConnected: profile.apple_music_connected,
          });
        }
      }
    } catch (error) {
      console.error('Error loading user:', error);
      if (error instanceof Error && error.message === 'User not found') {
        await handleDeletedUser();
      } else {
        setError('Failed to load user profile. Please try again.');
      }
    } finally {
      setLoading(false);
      setAuthChecked(true);
    }
  };

  useEffect(() => {
    initUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session) {
        setLoading(true);
        try {
          const profile = await fetchOrCreateUser(session.user.id);

          if (profile) {
            setUser({
              id: profile.id,
              username: profile.username,
              displayName: profile.display_name,
              bio: profile.bio,
              avatarUrl: profile.avatar_url,
              coverImage: profile.cover_image,
              spotifyConnected: profile.spotify_connected,
              appleMusicConnected: profile.apple_music_connected,
            });
          }
        } catch (error) {
          console.error('Error loading user after sign in:', error);
          if (error instanceof Error && error.message === 'User not found') {
            await handleDeletedUser();
          } else {
            setError('Failed to load user profile. Please try again.');
          }
        } finally {
          setLoading(false);
        }
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        setError(null);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const updateProfile = async (data: Partial<UserContextType['user']>) => {
    if (!user?.id) return;

    try {
      const { error } = await supabase
        .from('users')
        .update({
          username: data.username,
          display_name: data.displayName,
          bio: data.bio,
          avatar_url: data.avatarUrl,
          cover_image: data.coverImage,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);

      if (error) throw error;

      setUser(prev => prev ? { ...prev, ...data } : null);
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  };

  const retryLoading = async () => {
    setLoading(true);
    await initUser();
  };

  // Don't render anything until we've checked auth
  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <UserContext.Provider value={{ user, loading, error, updateProfile, retryLoading }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}